import React, { useEffect, useState } from "react";
import { useApp } from "@/contexts/AppContext";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader, StatusBadge } from "@/components/common/Bits";
import { api, formatError } from "@/lib/api";
import { Save, Moon, Sun, Globe, Shield, ShieldCheck, ShieldOff } from "lucide-react";
import { toast } from "sonner";

export default function Settings() {
  const { t, lang, setLang, theme, setTheme } = useApp();
  const { user, updateProfile, refresh } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [saving, setSaving] = useState(false);

  // 2FA state
  const [twofaSetup, setTwofaSetup] = useState(null); // {qr, secret}
  const [code, setCode] = useState("");
  const [working, setWorking] = useState(false);
  const enabled = !!user?.twofa_enabled;

  const save = async () => {
    setSaving(true);
    try { await updateProfile({ name, language: lang, theme }); toast.success(t("saved")); }
    catch { toast.error("Σφάλμα"); }
    finally { setSaving(false); }
  };

  const startTwofa = async () => {
    setWorking(true);
    try {
      const { data } = await api.post("/auth/2fa/setup");
      setTwofaSetup(data);
    } catch (e) { toast.error(formatError(e)); }
    finally { setWorking(false); }
  };

  const verifyTwofa = async () => {
    setWorking(true);
    try {
      await api.post("/auth/2fa/verify", { code });
      toast.success("2FA ενεργοποιήθηκε");
      setTwofaSetup(null); setCode("");
      await refresh();
    } catch (e) { toast.error(formatError(e)); }
    finally { setWorking(false); }
  };

  const disableTwofa = async () => {
    if (!code) { toast.error("Εισάγετε κωδικό για επιβεβαίωση"); return; }
    setWorking(true);
    try {
      await api.post("/auth/2fa/disable", { code });
      toast.success("2FA απενεργοποιήθηκε");
      setCode("");
      await refresh();
    } catch (e) { toast.error(formatError(e)); }
    finally { setWorking(false); }
  };

  return (
    <div data-testid="settings-page">
      <PageHeader title="Ρυθμίσεις" subtitle="Προσαρμογή προφίλ, εμφάνισης και ασφάλειας."/>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="surface-card p-6">
          <h3 className="font-heading font-semibold text-[14px] mb-4">{t("account")}</h3>
          <div className="space-y-4">
            <Row label="Email" value={<span className="font-mono text-[13px]">{user?.email}</span>}/>
            <Row label={t("role")} value={<span className="font-mono text-[13px] uppercase gold-text">{user?.role}</span>}/>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-mono">{t("name")}</label>
              <input data-testid="settings-name-input" value={name} onChange={e => setName(e.target.value)}
                className="mt-1.5 w-full bg-muted/30 border border-border/80 rounded-md px-3 py-2 text-[13px] focus:border-[hsl(var(--gold)/0.5)] outline-none"/>
            </div>
            <button data-testid="settings-save-btn" onClick={save} disabled={saving}
              className="flex items-center gap-1.5 px-4 py-2 rounded-md bg-[hsl(var(--gold))] text-[hsl(var(--background))] font-medium text-[13px]">
              <Save className="size-4"/>{saving ? t("loading") : t("save")}
            </button>
          </div>
        </div>

        <div className="surface-card p-6">
          <h3 className="font-heading font-semibold text-[14px] mb-4">{t("appearance")}</h3>
          <div className="space-y-5">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-mono mb-2 block">Θέμα</label>
              <div className="flex gap-2">
                <Toggle active={theme === "dark"} onClick={() => setTheme("dark")} icon={Moon} label={t("dark_mode")} testid="settings-theme-dark"/>
                <Toggle active={theme === "light"} onClick={() => setTheme("light")} icon={Sun} label={t("light_mode")} testid="settings-theme-light"/>
              </div>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-mono mb-2 block">{t("language")}</label>
              <div className="flex gap-2">
                <Toggle active={lang === "el"} onClick={() => setLang("el")} icon={Globe} label="Ελληνικά" testid="settings-lang-el"/>
                <Toggle active={lang === "en"} onClick={() => setLang("en")} icon={Globe} label="English" testid="settings-lang-en"/>
              </div>
            </div>
          </div>
        </div>

        {/* 2FA */}
        <div className="surface-card p-6 lg:col-span-2" data-testid="settings-2fa">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-heading font-semibold text-[14px] flex items-center gap-2">
                {enabled ? <ShieldCheck className="size-4 gold-text"/> : <Shield className="size-4 text-muted-foreground"/>}
                Two-Factor Authentication (2FA)
              </h3>
              <p className="text-[12px] text-muted-foreground mt-1">Επιπλέον επίπεδο ασφάλειας με authenticator app (Google Authenticator, 1Password, Authy).</p>
            </div>
            {enabled ? <StatusBadge tone="strong">ΕΝΕΡΓΟ</StatusBadge> : <StatusBadge tone="neutral">ΑΝΕΝΕΡΓΟ</StatusBadge>}
          </div>

          {!enabled && !twofaSetup && (
            <button data-testid="2fa-start-btn" onClick={startTwofa} disabled={working}
              className="px-4 py-2 rounded-md bg-[hsl(var(--gold))] text-[hsl(var(--background))] font-medium text-[13px] disabled:opacity-60">
              <Shield className="size-4 inline mr-1.5"/>Ενεργοποίηση 2FA
            </button>
          )}

          {!enabled && twofaSetup && (
            <div className="flex flex-col md:flex-row gap-6 items-start">
              <div className="bg-white p-2 rounded-lg border border-border/80" data-testid="2fa-qr">
                <img src={twofaSetup.qr} alt="QR" className="size-44"/>
              </div>
              <div className="flex-1">
                <p className="text-[13px] mb-3">1) Σκανάρετε τον QR κωδικό με την authenticator app σας</p>
                <p className="text-[13px] mb-1">2) Εισάγετε τον 6-ψήφιο κωδικό:</p>
                <div className="font-mono text-[11px] text-muted-foreground mb-3 break-all">Secret: {twofaSetup.secret}</div>
                <div className="flex gap-2">
                  <input
                    data-testid="2fa-code-input"
                    value={code} onChange={e => setCode(e.target.value)}
                    placeholder="123456" maxLength={6} autoFocus
                    className="w-32 bg-muted/30 border border-border/80 rounded-md px-3 py-2 text-[16px] font-mono tracking-widest text-center focus:border-[hsl(var(--gold)/0.5)] outline-none"/>
                  <button data-testid="2fa-verify-btn" onClick={verifyTwofa} disabled={working || code.length < 6}
                    className="px-4 py-2 rounded-md bg-[hsl(var(--gold))] text-[hsl(var(--background))] font-medium text-[13px] disabled:opacity-60">
                    Επιβεβαίωση
                  </button>
                  <button onClick={() => { setTwofaSetup(null); setCode(""); }}
                    className="px-3 py-2 rounded-md border border-border/80 text-[13px] text-muted-foreground hover:text-foreground">
                    Ακύρωση
                  </button>
                </div>
              </div>
            </div>
          )}

          {enabled && (
            <div className="flex gap-2 items-end">
              <div>
                <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-mono">Κωδικός για απενεργοποίηση</label>
                <input
                  data-testid="2fa-disable-code"
                  value={code} onChange={e => setCode(e.target.value)}
                  placeholder="123456" maxLength={6}
                  className="mt-1.5 w-32 bg-muted/30 border border-border/80 rounded-md px-3 py-2 text-[16px] font-mono tracking-widest text-center"/>
              </div>
              <button data-testid="2fa-disable-btn" onClick={disableTwofa} disabled={working}
                className="px-4 py-2 rounded-md border border-red-500/40 text-red-400 hover:bg-red-500/10 font-medium text-[13px]">
                <ShieldOff className="size-4 inline mr-1.5"/>Απενεργοποίηση
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-mono">{label}</div>
      <div className="mt-1">{value}</div>
    </div>
  );
}
function Toggle({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2 rounded-md border text-[13px] transition-colors ${active ? "border-[hsl(var(--gold)/0.6)] bg-[hsl(var(--gold)/0.08)] gold-text" : "border-border/60 hover:border-[hsl(var(--gold)/0.3)]"}`}>
      <Icon className="size-4"/>{label}
    </button>
  );
}
